import React, { useState, useEffect, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  MiniMap,
} from '@xyflow/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import '@xyflow/react/dist/style.css';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import { v4 as uuidv4 } from 'uuid';
import ChatbotAPI from './ChatbotAPI';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';
import './ChatbotFlowEditor.css';

const nodeTypes = { custom: CustomNode };

// CONTENT_BLOCKS constant removed as per requirements

// ContentBlockModal component removed as per requirements

function ChatbotFlowEditor({ chatbotId, onClose, onSave }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chatbotData, setChatbotData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);

  const edgeTypes = {
    custom: (props) => <CustomEdge {...props} setEdges={setEdges} />
  };

  // Load existing chatbot data
  useEffect(() => {
    loadChatbotData();
  }, [chatbotId]);

  const loadChatbotData = async () => {
    try {
      setLoading(true);
      const data = await ChatbotAPI.getChatbot(chatbotId);
      if (data) {
        setChatbotData(data);
        // Convert chatbot_node JSON to ReactFlow format
        const { flowNodes, flowEdges } = convertChatbotToFlow(data);
        
        // Ensure there's at least one start node
        if (flowNodes.length === 0 || !flowNodes.some(node => node.data.type === 'start')) {
          const startNodeId = 'default-start';
          flowNodes.unshift({
            id: startNodeId,
            type: 'custom',
            position: { x: 100, y: 100 },
            data: {
              type: 'start',
              message: 'Start Bot Flow',
              keywords: ['Hello', 'Hi', 'Start'],
              matchingType: 'exact',
              title: 'Welcome Bot',
              label: 'Start',
              onEdit: () => editNode(startNodeId),
              onDelete: () => deleteNode(startNodeId),
            },
          });
        }
        
        setNodes(flowNodes);
        setEdges(flowEdges);
      }
    } catch (error) {
      console.error('Error loading chatbot data:', error);
      ErrorToastContainer('Failed to load chatbot data');
      
      // Create a default start node if loading fails
      const startNodeId = 'default-start';
      setNodes([{
        id: startNodeId,
        type: 'custom',
        position: { x: 100, y: 100 },
        data: {
          type: 'start',
          message: 'Start Bot Flow',
          keywords: ['Hello', 'Hi', 'Start'],
          matchingType: 'exact',
          title: 'Welcome Bot',
          label: 'Start',
          onEdit: () => editNode(startNodeId),
          onDelete: () => deleteNode(startNodeId),
        },
      }]);
      setEdges([]);
    } finally {
      setLoading(false);
    }
  };

  // Convert chatbot_node JSON to ReactFlow format
  const convertChatbotToFlow = (chatbot) => {
    const flowNodes = [];
    const flowEdges = [];
    let nodeCounter = 1;

    try {
      const chatbotNode = JSON.parse(chatbot.chatbot_node);
      const triggerKeywords = JSON.parse(chatbot.tgm);

      // Create start nodes for each trigger keyword
      triggerKeywords.forEach((keyword, keywordIndex) => {
        const startNodeId = `start-${keywordIndex}`;
        flowNodes.push({
          id: startNodeId,
          type: 'custom',
          position: { x: 100, y: 100 + keywordIndex * 150 },
          data: {
            type: 'start',
            message: `Trigger: ${keyword}`,
            keywords: [keyword],
            label: `Start - ${keyword}`,
            onEdit: () => editNode(startNodeId),
            onDelete: () => deleteNode(startNodeId),
          },
        });

        // Add nodes for this keyword's flow
        if (chatbotNode[keyword] && Array.isArray(chatbotNode[keyword])) {
          chatbotNode[keyword].forEach((node, nodeIndex) => {
            const nodeId = `${keyword}-${nodeIndex}`;
            const prevNodeId = nodeIndex === 0 ? startNodeId : `${keyword}-${nodeIndex - 1}`;
            
            flowNodes.push({
              id: nodeId,
              type: 'custom',
              position: { x: 400 + nodeIndex * 200, y: 100 + keywordIndex * 150 },
              data: {
                type: node.type || 'message',
                message: node.message || '',
                delay: node.delay || 0,
                label: `${node.type || 'message'} ${nodeIndex + 1}`,
                onEdit: () => editNode(nodeId),
                onDelete: () => deleteNode(nodeId),
              },
            });

            // Add edge from previous node
            if (nodeIndex > 0 || keywordIndex === 0) {
              flowEdges.push({
                id: `edge-${prevNodeId}-${nodeId}`,
                source: prevNodeId,
                target: nodeId,
                type: 'custom',
              });
            }
          });
        }
      });
    } catch (error) {
      console.error('Error parsing chatbot data:', error);
      // Create a simple default flow
      flowNodes.push({
        id: 'start-1',
        type: 'custom',
        position: { x: 100, y: 100 },
        data: {
          type: 'start',
          message: 'Start Bot Flow',
          label: 'Start',
          onEdit: () => editNode('start-1'),
          onDelete: () => deleteNode('start-1'),
        },
      });
    }

    return { flowNodes, flowEdges };
  };

  // Convert ReactFlow format back to chatbot_node JSON
  const convertFlowToChatbot = () => {
    const chatbotNode = {};
    const triggerKeywords = [];

    // Find start nodes and their keywords
    nodes.forEach(node => {
      if (node.data.type === 'start' && node.data.keywords) {
        node.data.keywords.forEach(keyword => {
          if (!triggerKeywords.includes(keyword)) {
            triggerKeywords.push(keyword);
            chatbotNode[keyword] = [];
          }
        });
      }
    });

    // Build flow for each keyword
    triggerKeywords.forEach(keyword => {
      const keywordNodes = [];
      let currentNode = nodes.find(n => n.data.type === 'start' && n.data.keywords?.includes(keyword));
      
      while (currentNode) {
        const edge = edges.find(e => e.source === currentNode.id);
        if (edge) {
          const nextNode = nodes.find(n => n.id === edge.target);
          if (nextNode && nextNode.data.type !== 'start') {
            keywordNodes.push({
              node_id: keywordNodes.length + 1,
              next_node_id: keywordNodes.length + 2,
              type: nextNode.data.type || 'message',
              message: nextNode.data.message || '',
              delay: nextNode.data.delay || 0,
            });
          }
          currentNode = nextNode;
        } else {
          // Last node
          if (currentNode.data.type !== 'start') {
            keywordNodes.push({
              node_id: keywordNodes.length + 1,
              next_node_id: null,
              type: currentNode.data.type || 'message',
              message: currentNode.data.message || '',
              delay: currentNode.data.delay || 0,
            });
          }
          break;
        }
      }
      
      chatbotNode[keyword] = keywordNodes;
    });

    return {
      chatbot_node: JSON.stringify(chatbotNode),
      tgm: JSON.stringify(triggerKeywords),
    };
  };

  // addNode function removed as per requirements - no longer needed

  const editNode = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNode(node);
      setShowNodeEditor(true);
    }
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  };

  const updateNode = (nodeId, updatedData) => {
    setNodes(nds =>
      nds.map(n =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...updatedData } }
          : n
      )
    );
    setShowNodeEditor(false);
    setSelectedNode(null);
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const saveFlow = async () => {
    try {
      setSaving(true);
      const flowData = convertFlowToChatbot();
      
      const updatedData = {
        ...chatbotData,
        ...flowData,
      };

      const result = await ChatbotAPI.updateChatbot(chatbotId, updatedData);
      if (result) {
        SuccessToastContainer('Chatbot flow saved successfully!');
        if (onSave) onSave(result);
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      ErrorToastContainer('Failed to save chatbot flow');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="chatbot-flow-editor-loading">
        <div className="loading-spinner"></div>
        <p>Loading chatbot data...</p>
      </div>
    );
  }

  return (
    <div className="chatbot-flow-editor">
      {/* Header */}
      <div className="chatbot-flow-editor-header">
        <div className="chatbot-flow-editor-title">
          <h3>Edit Chatbot Flow</h3>
          {chatbotData && (
            <p className="chatbot-flow-editor-subtitle">
              {chatbotData.chatbot_name}
            </p>
          )}
        </div>
        <div className="chatbot-flow-editor-actions">
          <button
            className="chatbot-flow-editor-btn chatbot-flow-editor-btn-secondary"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </button>
          <button
            className="chatbot-flow-editor-btn chatbot-flow-editor-btn-primary"
            onClick={saveFlow}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} />
            {saving ? 'Saving...' : 'Save Flow'}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="chatbot-flow-editor-toolbar">
        <div className="chatbot-flow-editor-info">
          <span>Nodes: {nodes.length}</span>
          <span>Edges: {edges.length}</span>
        </div>
      </div>

      {/* Flow Canvas */}
      <div className="chatbot-flow-editor-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>

      {/* Content Block Modal - Removed as per requirements */}

      {/* Node Editor Modal */}
      {showNodeEditor && selectedNode && (
        <NodeEditorModal
          node={selectedNode}
          onSave={(updatedData) => updateNode(selectedNode.id, updatedData)}
          onClose={() => {
            setShowNodeEditor(false);
            setSelectedNode(null);
          }}
        />
      )}
    </div>
  );
}

// Node Editor Modal Component
function NodeEditorModal({ node, onSave, onClose }) {
  const [formData, setFormData] = useState({
    message: node.data.message || '',
    delay: node.data.delay || 0,
    mediaUrl: node.data.mediaUrl || '',
    mediaType: node.data.mediaType || 'image',
    listOptions: node.data.listOptions || ['Option 1'],
    condition: node.data.condition || '',
    apiUrl: node.data.apiUrl || '',
    apiMethod: node.data.apiMethod || 'GET',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const addListOption = () => {
    setFormData(prev => ({
      ...prev,
      listOptions: [...prev.listOptions, `Option ${prev.listOptions.length + 1}`],
    }));
  };

  const removeListOption = (index) => {
    setFormData(prev => ({
      ...prev,
      listOptions: prev.listOptions.filter((_, i) => i !== index),
    }));
  };

  // Button functionality removed - buttons handled by dedicated button nodes

  return (
    <div className="node-editor-modal-overlay">
      <div className="node-editor-modal">
        <div className="node-editor-modal-header">
          <h4>Edit {node.data.type} Node</h4>
          <button className="node-editor-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="node-editor-form">
          <div className="form-group">
            <label>Message:</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Enter message..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Delay (seconds):</label>
            <input
              type="number"
              value={formData.delay}
              onChange={(e) => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) || 0 }))}
              min="0"
              step="0.1"
            />
          </div>

          {/* Conditional fields based on node type */}
          {node.data.type === 'media' && (
            <div className="form-group">
              <label>Media URL:</label>
              <input
                type="url"
                value={formData.mediaUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, mediaUrl: e.target.value }))}
                placeholder="https://example.com/image.jpg"
              />
              <select
                value={formData.mediaType}
                onChange={(e) => setFormData(prev => ({ ...prev, mediaType: e.target.value }))}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
            </div>
          )}

          {node.data.type === 'list' && (
            <div className="form-group">
              <label>List Options:</label>
              {formData.listOptions.map((option, index) => (
                <div key={index} className="list-option-item">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...formData.listOptions];
                      newOptions[index] = e.target.value;
                      setFormData(prev => ({ ...prev, listOptions: newOptions }));
                    }}
                    placeholder={`Option ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeListOption(index)}
                    className="remove-btn"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addListOption} className="add-btn">
                <FontAwesomeIcon icon={faPlus} />
                Add Option
              </button>
            </div>
          )}

          {/* Button section removed - buttons handled by dedicated button nodes */}

          {node.data.type === 'condition' && (
            <div className="form-group">
              <label>Condition:</label>
              <textarea
                value={formData.condition}
                onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                placeholder="Enter condition (e.g., user_input === 'yes')"
                rows={2}
              />
            </div>
          )}

          {node.data.type === 'api_call' && (
            <div className="form-group">
              <label>API URL:</label>
              <input
                type="url"
                value={formData.apiUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, apiUrl: e.target.value }))}
                placeholder="https://api.example.com/endpoint"
              />
              <select
                value={formData.apiMethod}
                onChange={(e) => setFormData(prev => ({ ...prev, apiMethod: e.target.value }))}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
          )}

          <div className="node-editor-modal-footer">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn">
              <FontAwesomeIcon icon={faSave} />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChatbotFlowEditor;
